import React, { Component } from 'react';
import _ from 'lodash';
import html2canvas from 'html2canvas';
import moment from 'moment';
import CircularProgress from '@material-ui/core/CircularProgress';
import PropTypes from 'prop-types';
import TableHeader from './Components/TableHeader/TableHeader';
import { LastRefresh } from './Components/LastRefresh';
import NextAppointments from './Components/NextAppointments';
import Filters from './Components/Filters';
import { GAsearchDelay, GET_EMPTY_APPOINTMENTS_INTERVAL } from './constants/appointments.constants';
import StopTheClockModal from './Components/StopTheClockModal';
import StopTheClockFeedbackMsg from './Components/StopTheClockFeedbackMsg';
import { NextAppointmentsService } from './Components/NextAppointments/services/next-appointments.service';
import { SegmentService } from 'services/helpers/segment';
import Legend from './Components/Legend/Legend';
import { CELL_WIDTH_192, CELL_WIDTH_130 } from 'constants/layout';
import { PageHeader } from 'layouts/Generic/page-header';
import { Divider } from '@mui/material';
import { Page } from 'components/Page';

export default class Appointments extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    getAppointmentsAvailabilityCredentials: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    availabilityCredentials: PropTypes.object.isRequired,
    clearAvailabilityCredentials: PropTypes.func,
    history: PropTypes.object,
    refreshToken: PropTypes.bool,
    startLoadingOnDownload: PropTypes.func.isRequired,
    endLoadingOnDownload: PropTypes.func.isRequired,
    getEaScreenshotPDF: PropTypes.func.isRequired,
    getScreenshotsBySL: PropTypes.func.isRequired,
    closeLegend: PropTypes.func.isRequired,
    getRecipients: PropTypes.func.isRequired,
    region: PropTypes.object.isRequired,
    showCurrentShiftOnly: PropTypes.bool,
    forDatetime: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      isDownloadInProgress: false,
      isFilterMenuOpen: false,
      port: '',
      shift: {},
      shippingLinesToTerminals: [],
      currentTime: this.getCurrentDate(),
      isStopTheClockModalOpen: false,
      stopTheClockFeedbackMsg: '',
      isAppointmentsLocked: false,
      isLegendOpen: false,
    };
    this.availabilityTimerId = null;
    this.GAtimer = true;
    this.containerNumberInput = '';
    this.containerNumberWasSuccessfullySubmitted = false;
  }

  initEmptyAppointments = () => {
    const { region, forDatetime } = this.props;
    setTimeout(() => {
      this.GAtimer = false;
    }, GAsearchDelay);

    this.props.getAppointmentsAvailabilityCredentials(region.code);

    this.availabilityTimerId = setInterval(() => {
      this.props.getAppointmentsAvailabilityCredentials(region.code);
    }, GET_EMPTY_APPOINTMENTS_INTERVAL);

    if (!forDatetime) {
      this.createCurrentTimeInterval();
    }
  };

  clearCurrentTimeInterval = () => {
    if (this.timerCurrentDate) {
      clearInterval(this.timerCurrentDate);
      this.timerCurrentDate = null;
    }
  };

  createCurrentTimeInterval = () => {
    this.clearCurrentTimeInterval();
    this.timerCurrentDate = setInterval(() => {
      this.setState({ currentTime: this.getCurrentDate() });
    }, GET_EMPTY_APPOINTMENTS_INTERVAL);
  };

  componentDidMount() {
    this.initEmptyAppointments();
    window.scrollTo(0, 0);
  }

  componentDidUpdate(prevProps) {
    const { refreshToken, region, forDatetime } = this.props;
    if (prevProps.refreshToken !== refreshToken) {
      this.props.getAppointmentsAvailabilityCredentials(region.code);
    }

    if (!_.isEqual(forDatetime, prevProps.forDatetime)) {
      if (_.isNil(forDatetime)) {
        this.createCurrentTimeInterval();
      } else {
        this.clearCurrentTimeInterval();
      }
    }
  }

  getCurrentDate = () => {
    const { region } = this.props;
    return moment().tz(region.timeZone).format('dddd, MMMM DD, hh:mm A z');
  };

  handleStopTheClockModalOpen = () => {
    const { shippingLine, containerType } = this.props;
    this.setState({ isStopTheClockModalOpen: true });
    SegmentService.trackEventNoSlack('Stop-the-clock-button Clicked', {
      shippingLine,
      containerType,
      email: this.props.user.email,
    });
  };

  handleStopTheClockModalClose = () => {
    this.setState({ isStopTheClockModalOpen: false });
  };

  handleGetReportClick = (isDownloadReport, isSendCopy, containersInput, selectedTeamMembers) => {
    this.getScreenshot(isDownloadReport, isSendCopy, containersInput, selectedTeamMembers);
    this.setState({ isStopTheClockModalOpen: false });
  };

  getTerminalByShippingLine = () => {
    const { context, shippingLine } = this.props;
    if (!shippingLine || !context.terminals.length) return [];

    const map = context.shippingLineMapping
      .filter((item) => item.shipping_line === shippingLine)
      .map((i) => i.port);
    const filteredTerminals = context.terminals.filter((item) => map.includes(item.key));

    const filteredTerminalsBySL = filteredTerminals.filter((terminal) => {
      const appointmentsByTerminal =
        context && context.appointments && context.appointments[terminal.key];
      return appointmentsByTerminal && appointmentsByTerminal[shippingLine];
    });

    return filteredTerminalsBySL;
  };

  showLastRefresh = () => {
    const { user, context, availabilityCredentials, region, shippingLine } = this.props;
    if (!shippingLine) return;
    return (
      <LastRefresh
        user={user}
        terminals={this.getTerminalByShippingLine()}
        displayShift={false}
        selectedSlKey={shippingLine}
        lastRefreshData={context.appointments}
        availabilityCredentials={availabilityCredentials}
        region={region}
      />
    );
  };

  renderTableHeader = () => {
    const { shippingLine, containerType, context, forDatetime, region } = this.props;
    const { currentTime } = this.state;
    const terminalsList = context.terminals && Object.values(context.terminals).flat();

    return shippingLine ? (
      <TableHeader
        terminalsCount={terminalsList && terminalsList.length}
        displayShift={false}
        terminalMap={this.getTerminalByShippingLine()}
        shippingLine={shippingLine}
        containerType={containerType}
        currentTime={
          forDatetime?.tz(region.timeZone, true).format('dddd, MMMM DD, hh:mm A z') || currentTime
        }
        fetching={context.fetching}
      />
    ) : null;
  };

  showNextAppointments = () => {
    const {
      user,
      history,
      availabilityCredentials,
      showCurrentShiftOnly,
      containerType,
      shippingLine,
    } = this.props;
    if (!shippingLine) return;
    return (
      <NextAppointments
        user={user}
        history={history}
        terminals={this.getTerminalByShippingLine()}
        currentSLKey={shippingLine || ''}
        currentContainerKey={containerType || ''}
        availabilityCredentials={availabilityCredentials}
        showCurrentShiftOnly={showCurrentShiftOnly}
        forDatetime={this.props.forDatetime}
      />
    );
  };

  handleOpenLegend = () => {
    this.setState({ isLegendOpen: true });
  };

  handleCloseLegend = () => {
    this.setState({ isLegendOpen: false });
  };

  showLegend = () => {
    const { isLegendOpen } = this.state;
    return (
      <Legend
        isLegendOpen={isLegendOpen}
        handleOpenLegend={() => this.setState({ isLegendOpen: true })}
        handleCloseLegend={() => this.setState({ isLegendOpen: false })}
      />
    );
  };

  showStopTheClockFeedbackMsg = (msg) => {
    this.setState({ stopTheClockFeedbackMsg: msg });
    setTimeout(() => this.setState({ stopTheClockFeedbackMsg: '' }), 2000);
  };

  getScreenshot = (isDownloadReport, isSendCopy, containersInput, selectedTeamMembers) => {
    const {
      user,
      isLoading,
      closeLegend,
      getEaScreenshotPDF,
      getScreenshotsBySL,
      startLoadingOnDownload,
      endLoadingOnDownload,
      region,
      shippingLine,
      containerType,
      context,
    } = this.props;
    const { isDownloadInProgress } = this.state;
    if (isDownloadInProgress || isLoading || context.loading) return;
    closeLegend();

    NextAppointmentsService.fireGADownloadEvent(shippingLine, containerType, user, containersInput);
    SegmentService.trackEventNoSlack('Get Report Button Clicked', {
      shippingLine: shippingLine,
      containerType: containerType,
      containersAdded: containersInput,
      selectedTeamMembers: selectedTeamMembers,
      numberOfEmails: selectedTeamMembers.length,
      isDownloadReport: isDownloadReport,
      isSendCopy: isSendCopy,
      email: user.email,
    });

    this.setState({ isDownloadInProgress: true });
    startLoadingOnDownload();
    getScreenshotsBySL(shippingLine, region, (data) => {
      const screenshotKeys = data
        .filter((terminal) => terminal && terminal.key)
        .map((terminal) => terminal.key);
      const targetDiv = document.querySelector('#capture');
      html2canvas(targetDiv, {
        useCORS: true,
        allowTaint: true,
        width: 1400,
        height: 2100,
      }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const requestBody = {
          screenshotKeys,
          isEmptyAppointmentsTableImg: imgData,
          containersInput,
          metaData: {
            dateTime: moment().tz(region.timeZone).format('YYYY-MM-DD hh:mm A'),
            size: containerType,
            containerNumber: this.containerNumberInput,
            shippingLine: shippingLine,
          },
          download: isDownloadReport,
          email: isSendCopy,
          emails: selectedTeamMembers,
          port_collection_code: region.code,
        };
        const fileNameData = {
          shippingLineKey: shippingLine,
          containerTypeName: containerType,
          containerNumber: this.containerNumberInput,
        };
        getEaScreenshotPDF(requestBody, fileNameData, region, (error) => {
          this.setState({ isDownloadInProgress: false });
          endLoadingOnDownload();
          if (error) return;
          let feedbackMsg;
          if (isDownloadReport && isSendCopy) {
            feedbackMsg = 'Report succesfully downloaded and sent by email!';
          } else if (isDownloadReport) {
            feedbackMsg = 'Report succesfully downloaded!';
          } else if (isSendCopy) {
            feedbackMsg = 'Report successfully sent by email!';
          } else {
            feedbackMsg = '';
          }
          this.showStopTheClockFeedbackMsg(feedbackMsg);
        });
      });
    });
  };

  render() {
    const { classes, isLoading, context, user, getRecipients, shippingLine, containerType } =
      this.props;

    const { email, teamMembers } = user || {};

    const { isDownloadInProgress, isStopTheClockModalOpen, stopTheClockFeedbackMsg } = this.state;

    const startWidth = this.getTerminalByShippingLine().length * CELL_WIDTH_130 + CELL_WIDTH_192;
    const captureWidth = Math.max(1024, startWidth);

    return (
      <Page className="py-8 px-16 relative">
        {isStopTheClockModalOpen && (
          <StopTheClockModal
            email={email}
            teamMembers={teamMembers}
            getRecipients={getRecipients}
            onHandleGetReportClick={this.handleGetReportClick}
            onHandleStopTheClockModalClose={this.handleStopTheClockModalClose}
          />
        )}
        {stopTheClockFeedbackMsg && (
          <StopTheClockFeedbackMsg stopTheClockFeedbackMsg={stopTheClockFeedbackMsg} />
        )}
        {(isLoading || context.loading) && (
          <div className={classes.loaderWrapper}>
            <CircularProgress size={70} />
          </div>
        )}
        <div className="space-y-4">
          <PageHeader
            title="Empty Appointments"
            description="Find Appointments for your singles and duals"
          />
          <Divider />
          <Filters
            isLoading={isLoading || context.loading}
            isDownloadInProgress={isDownloadInProgress}
            onHandleStopTheClockModalOpen={this.handleStopTheClockModalOpen}
          />
          {shippingLine && containerType && (
            <div className="z-20">
              <div
                style={{
                  width: `${captureWidth}px`,
                }}
                id="capture"
              >
                {this.renderTableHeader()}
                {this.showLastRefresh()}
                {this.showNextAppointments()}
              </div>
              {this.showLegend()}
            </div>
          )}
        </div>
      </Page>
    );
  }

  componentWillUnmount() {
    const { clearAvailabilityCredentials } = this.props;
    clearAvailabilityCredentials();
    clearInterval(this.availabilityTimerId);
    this.clearCurrentTimeInterval();
  }
}
